import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useIndividualsData } from './hooks/useIndividualsData';
import { useFilters } from './hooks/useFilters';
import { IndividualsTable } from "./IndividualsTable";
import { IndividualsGrid } from "./IndividualsGrid";
import { PaginationControls } from "./PaginationControls";
import { FilterBar, FilterSidebar } from "./FilterBar";
import AddIndividual from "./AddIndividual";
import SendEmailModal from "../../components/SendEmailModal";
import PdfDropZone from "../../components/PdfDropZone";
import BulkActionPanel from "./BulkActionPanel";
import ConfirmationDialog from "../utils/ConfirmationDialog";
import { useNavigate } from "react-router-dom";
import { ApiContext } from "../../App";
import { getTagStyle } from "../../styles/tagUtils";
import Loading from "../utils/Loading";
import Breadcrumb from "../utils/Breadcrumb";
import { toast } from "react-toastify";
import { getAllEmails } from "../../utils/emailHelpers";


// ===== COMPOSANT PRINCIPAL OPTIMISÉ =====
const AllIndividuals = () => {
  const navigate = useNavigate();
  const API = useContext(ApiContext);
  
  // État pour le type de vue avec logique responsive
  const [viewType, setViewType] = useState('grid'); // 'table' ou 'grid'
  const [screenSize, setScreenSize] = useState('desktop'); // 'mobile', 'tablet', 'desktop'
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0});
  const [showFilters, setShowFilters] = useState(false); // Pour mobile/tablet
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [individualToDelete, setIndividualToDelete] = useState(null);

  // Détecter la taille d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
        setViewType('table'); // Force la vue cartes sur mobile
      } else if (width < 1280) {
        setScreenSize('tablet');
        // Garde la vue actuelle sur tablette/petit desktop
      } else {
        setScreenSize('desktop');
        // Garde la vue actuelle sur grand desktop
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Custom hooks pour séparer la logique
  const {
    individuals,
    setIndividuals,
    loading,
    positions,
    candidateMap,
    bizdevProcesses,
    bizdevProcessMap,
    fetchIndividuals,
    fetchPositions,
    fetchBizdevProcesses,
    deleteIndividual
  } = useIndividualsData();

  // ✅ CORRECTION: Passer candidateMap et bizdevProcessMap au hook useFilters
  const {
    searchTerm,
    setSearchTerm,
    selectedTags,
    setSelectedTags,
    selectedPositions,
    setSelectedPositions,
    showOnlyWithPhone,
    setShowOnlyWithPhone,
    showOnlyWithProfessionalEmail,
    setShowOnlyWithProfessionalEmail,
    showOnlyWithPersonalEmail,
    setShowOnlyWithPersonalEmail,
    sortOrder,
    setSortOrder,
    sortBy,
    setSortBy,
    filteredIndividuals,
    // Nouveaux filtres
    cityFilter,
    setCityFilter,
    personalEmailFilter,
    setPersonalEmailFilter,
    professionalEmailFilter,
    setProfessionalEmailFilter,
    jobTitleFilter,
    setJobTitleFilter,
    keywordFilter,
    setKeywordFilter,
    yearsOfExperienceFilter,
    setYearsOfExperienceFilter,
    salaryFilters,
    setSalaryFilters,
    openToJobOpportunities,
    setOpenToJobOpportunities,
    selectedBizdevProcesses,
    setSelectedBizdevProcesses,
  } = useFilters(individuals, candidateMap, bizdevProcessMap);

  // États pour la pagination et la sélection
  const [currentPage, setCurrentPage] = useState(1);
  const [individualsPerPage, setIndividualsPerPage] = useState(20);
  const [selectedIndividuals, setSelectedIndividuals] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [emailTypeForSending, setEmailTypeForSending] = useState('professional');
  const [filteredRecipientsForEmail, setFilteredRecipientsForEmail] = useState([]);

  // Ajuster le nombre d'éléments par page selon la vue
  const itemsPerPage = useMemo(() => {
    return viewType === 'grid' ? 24 : individualsPerPage; // Plus d'éléments pour la grille
  }, [viewType, individualsPerPage]);

  // Données paginées mémorisées
  const displayedIndividuals = useMemo(() => {
    const indexOfLastIndividual = currentPage * itemsPerPage;
    const indexOfFirstIndividual = indexOfLastIndividual - itemsPerPage;
    return filteredIndividuals.slice(indexOfFirstIndividual, indexOfLastIndividual);
  }, [filteredIndividuals, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredIndividuals.length / itemsPerPage);

  // Gestionnaires d'événements optimisés avec useCallback
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, [setSearchTerm]);

  const handleIndividualClick = useCallback((individualId) => {
    const navigationState = {
      filteredIndividuals: filteredIndividuals,
      filters: {
        selectedTags,
        selectedPositions,
        searchTerm,
        showOnlyWithPhone
      }
    };
    navigate(`/all-individuals/${individualId}`, { state: navigationState });
  }, [navigate, filteredIndividuals, selectedTags, selectedPositions, searchTerm, showOnlyWithPhone]);

  const handleSelectAll = useCallback(() => {
    setSelectAll(prev => {
      const newSelectAll = !prev;
      if (newSelectAll) {
        setSelectedIndividuals(displayedIndividuals.map(ind => ind._id));
      } else {
        setSelectedIndividuals([]);
      }
      return newSelectAll;
    });
  }, [displayedIndividuals]);

  const handleIndividualSelect = useCallback((individualId) => {
    setSelectedIndividuals(prev =>
      prev.includes(individualId)
        ? prev.filter(id => id !== individualId)
        : [...prev, individualId]
    );
  }, []);

  // Ouvrir le dialogue de confirmation de suppression
  const handleDelete = useCallback((individualId) => {
    setIndividualToDelete(individualId);
    setShowDeleteDialog(true);
  }, []);

  // Confirmer et effectuer la suppression
  const confirmDelete = useCallback(async () => {
    if (individualToDelete) {
      await deleteIndividual(individualToDelete);
      // Mettre à jour la sélection si l'individu supprimé était sélectionné
      setSelectedIndividuals(prev => prev.filter(id => id !== individualToDelete));
      setShowDeleteDialog(false);
      setIndividualToDelete(null);
    }
  }, [individualToDelete, deleteIndividual]);

  // Gérer le traitement des PDFs
  const handlePdfProcessed = useCallback((individual, isNewIndividual) => {
    if (isNewIndividual) {
      // Ajouter le nouvel individu à la liste
      setIndividuals(prev => [individual, ...prev]);
    }
    // Vous pouvez ajouter d'autres logiques ici si nécessaire
  }, [setIndividuals]);

  // Gestionnaire pour changer le type de vue
  const handleViewChange = useCallback((newViewType) => {
    setViewType(newViewType);
    setCurrentPage(1); // Remettre à la première page quand on change de vue
  }, []);


  // Ouvrir la modale de confirmation pour la suppression en bulk
  const handleBulkDeleteRequest = useCallback((individualIds) => {
    setPendingDeleteIds(individualIds);
    setShowDeleteConfirm(true);
  }, []);

  // Bulk delete individuals (appelé après confirmation)
  const handleBulkDeleteConfirmed = useCallback(async () => {
    try {
      const response = await API.post('/api/individuals/bulk-delete', {
        individualIds: pendingDeleteIds,
      });

      if (response.data.success) {
        const { successCount, errorCount } = response.data.results;

        // Message de succès
        toast.success(
          `${successCount} profil(s) supprimé(s) avec succès${
            errorCount > 0 ? ` (${errorCount} erreur(s))` : ''
          }`,
          { autoClose: 5000 }
        );

        // Afficher les erreurs dans la console si présentes
        if (response.data.errors && response.data.errors.length > 0) {
          console.error('Erreurs lors de la suppression:', response.data.errors);
        }

        // Réinitialiser la sélection
        setSelectedIndividuals([]);
        setSelectAll(false);

        // Rafraîchir la liste des individuals
        await fetchIndividuals();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression en bulk:', error);
      toast.error(
        `Erreur lors de la suppression: ${
          error.response?.data?.message || 'Erreur inconnue'
        }`
      );
    }
  }, [API, fetchIndividuals, pendingDeleteIds]);

  const handleBulkAddToProcess = useCallback(async (individualIds, positionId, stageId) => {
    try {
      let addedCount = 0;
      let alreadyExistsCount = 0;
      const errors = [];

      // Ajouter chaque individual au poste avec l'étape spécifiée
      for (const individualId of individualIds) {
        try {
          await API.post(`/api/positions/${positionId}/candidates`, {
            individualId: individualId,
            stageId: stageId
          });
          addedCount++;
        } catch (err) {
          if (err.response?.status === 400 && err.response?.data?.message?.includes('already added')) {
            alreadyExistsCount++;
          } else {
            errors.push(`Erreur pour un profil: ${err.response?.data?.message || err.message}`);
          }
        }
      }

      // Afficher les résultats
      if (addedCount > 0) {
        toast.success(`${addedCount} profil(s) ajouté(s) au processus de recrutement`);
      }
      if (alreadyExistsCount > 0) {
        toast.info(`${alreadyExistsCount} profil(s) déjà présent(s) dans ce processus`);
      }
      if (errors.length > 0) {
        console.error('Erreurs lors de l\'ajout:', errors);
        toast.error(`${errors.length} erreur(s) lors de l'ajout`);
      }

      setSelectedIndividuals([]);
      setSelectAll(false);
      fetchIndividuals(); // Rafraîchir la liste
    } catch (error) {
      console.error('Erreur lors de l\'ajout au processus:', error);
      toast.error(`Erreur lors de l'ajout au processus: ${error.response?.data?.message || error.message}`);
    }
  }, [API, fetchIndividuals]);

  const handleBulkSendEmail = useCallback(async (emailType) => {
    // Récupérer les individus sélectionnés avec leurs infos complètes
    const selectedIndividualsData = individuals.filter(ind =>
      selectedIndividuals.includes(ind._id)
    );

    // Filtrer selon le type d'email demandé
    const validRecipients = selectedIndividualsData.filter(ind => {
      if (emailType === 'professional') {
        return getAllEmails(ind.professionalEmail).length > 0;
      } else if (emailType === 'personal') {
        return getAllEmails(ind.personalEmail).length > 0;
      }
      return false;
    });

    if (validRecipients.length === 0) {
      toast.warning(
        `Aucun des individus sélectionnés n'a d'email ${
          emailType === 'professional' ? 'professionnel' : 'personnel'
        }`
      );
      return;
    }

    // Si certains individus n'ont pas le type d'email demandé, afficher un avertissement
    const skippedCount = selectedIndividuals.length - validRecipients.length;
    if (skippedCount > 0) {
      toast.info(
        `${skippedCount} individu(s) n'ont pas d'email ${
          emailType === 'professional' ? 'professionnel' : 'personnel'
        } et seront ignorés`
      );
    }

    // Stocker uniquement les IDs des destinataires valides
    setFilteredRecipientsForEmail(validRecipients.map(ind => ind._id));
    setEmailTypeForSending(emailType);
    setShowSendEmailModal(true);
    setShowBulkActions(false);
  }, [selectedIndividuals, individuals]);

  const handleBulkAddTags = useCallback(async (individualIds, tagNames) => {
    try {
      // Utiliser le nouvel endpoint bulk
      const response = await API.post('/api/individuals/bulk-add-tags', {
        individualIds: individualIds,
        tagNames: tagNames
      });

      if (response.data.success) {
        const { results } = response.data;
        toast.success(`Tags ajoutés avec succès! (${results.successCount} opérations réussies sur ${results.totalOperations})`);
        
        if (results.errorCount > 0) {
          toast.warn('Certaines opérations ont échoué:', response.data.errors);
        }
        
        fetchIndividuals(); // Rafraîchir la liste
      } else {
        alert('Erreur lors de l\'ajout des tags');
      }

      setSelectedIndividuals([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout des tags:', error);
      alert(`Erreur lors de l\'ajout des tags: ${error.response?.data?.message || error.message}`);
    }
  }, [API, fetchIndividuals]);

  const handleBulkAddReminder = useCallback(async (individualIds, date, note = '') => {
    try {
      // Utiliser le nouvel endpoint bulk pour les tâches
      const response = await API.post('/api/bulk-reminders', {
        individualIds: individualIds,
        reminderDate: date,
        text: note || `Tâche pour les profils sélectionnés`
      });

      if (response.data.success) {
        const { results } = response.data;
        alert(`Tâches créées avec succès! (${results.successCount} tâches créées sur ${results.totalOperations} profils)`);
        
        if (results.errorCount > 0) {
          console.warn('Certaines opérations ont échoué:', response.data.errors);
        }
      } else {
        alert('Erreur lors de la création des tâches');
      }

      setSelectedIndividuals([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Erreur lors de la création des tâches:', error);
      alert(`Erreur lors de la création des tâches: ${error.response?.data?.message || error.message}`);
    }
  }, [API]);

  const handleBulkAddToBizDevProcess = useCallback(async (individualIds, processId, step) => {
    try {
      const response = await API.post(`/api/process-bizdev/${processId}/bulk-prospects`, {
        individualIds: individualIds,
        step: step,
        notes: 'Ajout en lot depuis la CVthèque'
      });

      if (response.data.success) {
        if (response.data.skippedCount > 0) {
          toast.error(`${response.data.skippedCount} profil(s) étaient déjà dans le processus`);
          setSelectedIndividuals([]);
          setSelectAll(false);
          return;
        }
        toast.success(`${response.data.addedCount} profil(s) ajouté(s) au projet`);
        
      } else {
        toast.error('Erreur lors de l\'ajout au processus BizDev');
      }

      setSelectedIndividuals([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout au processus BizDev:', error);
      alert(`Erreur lors de l\'ajout au processus BizDev: ${error.response?.data?.message || error.message}`);
    }
  }, [API]);

  // Chargement initial
  useEffect(() => {
    fetchIndividuals();
    fetchPositions();
    fetchBizdevProcesses();
  }, [fetchIndividuals, fetchPositions, fetchBizdevProcesses]);

  // Afficher le loading pendant le chargement
  if (loading) {
    return (
      <div className="bg-gray-100">
        <Loading message="Chargement de la CVthèque..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className=" pt-16 min-h-screen bg-gray-100">
      <div className="px-4">
        {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/app', icon: 'bi-house' },
        { label: 'CVthèque', href: null, icon: 'bi-people', isActive: true }
      ]} />
      </div>
      

      {/* ✅ AMÉLIORATION: Ajout du composant PdfDropZone */}
      <PdfDropZone onPdfProcessed={handlePdfProcessed} />
      
      {/* Header avec sélecteur de vue */}
      <div className="flex justify-between px-4 items-center mb-3">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold m-0">Contacts </h1>

          {/* Boutons pour changer de vue - Masqués sur mobile uniquement */}
          {screenSize !== 'mobile' && (
            <div className="flex bg-white rounded-lg border border-gray-200 p-1">
              <button
                onClick={() => handleViewChange('table')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewType === 'table'
                    ? 'bg-indigo-100 text-indigo-700 '
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="bi bi-table mr-2"></i>
                Tableau
              </button>
              <button
                onClick={() => handleViewChange('grid')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewType === 'grid'
                    ? 'bg-indigo-100 text-indigo-700 '
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="bi bi-grid-3x3-gap mr-2"></i>
                Grille
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Bouton filtres sur mobile/tablet */}
          {screenSize !== 'desktop' && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <i className="bi bi-funnel text-lg"></i>
              <span className="text-sm font-medium">Filtres</span>
            </button>
          )}

          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 
                     text-white rounded-lg font-medium transition-colors shadow-sm self-start sm:self-auto"
          >
            <i className="bi bi-plus-lg"></i>
            <span className="hidden sm:inline">Ajouter Contact</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
      </div>



      {uploadingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold mb-2">Import en cours...</h3>
              <p className="text-gray-600">Traitement du fichier CSV</p>
            </div>
          </div>
        </div>
      )}

      {/* Layout avec 2 colonnes : filtres à gauche, contenu à droite */}
      <div className="flex relative">
        {/* Sidebar des filtres - Desktop toujours visible, Mobile/Tablet en overlay */}
        {screenSize === 'desktop' ? (
          <FilterSidebar
            onTagFilter={setSelectedTags}
            positions={positions}
            onPositionFilter={setSelectedPositions}
            showOnlyWithPhone={showOnlyWithPhone}
            onPhoneFilter={setShowOnlyWithPhone}
            showOnlyWithProfessionalEmail={showOnlyWithProfessionalEmail}
            onProfessionalEmailToggle={setShowOnlyWithProfessionalEmail}
            showOnlyWithPersonalEmail={showOnlyWithPersonalEmail}
            onPersonalEmailToggle={setShowOnlyWithPersonalEmail}
            currentTags={selectedTags}
            selectedPositions={selectedPositions}
            cityFilter={cityFilter}
            onCityFilter={setCityFilter}
            personalEmailFilter={personalEmailFilter}
            onPersonalEmailFilter={setPersonalEmailFilter}
            professionalEmailFilter={professionalEmailFilter}
            onProfessionalEmailFilter={setProfessionalEmailFilter}
            jobTitleFilter={jobTitleFilter}
            onJobTitleFilter={setJobTitleFilter}
            keywordFilter={keywordFilter}
            onKeywordFilter={setKeywordFilter}
            yearsOfExperienceFilter={yearsOfExperienceFilter}
            onYearsOfExperienceFilter={setYearsOfExperienceFilter}
            salaryFilters={salaryFilters}
            onSalaryFiltersChange={setSalaryFilters}
            openToJobOpportunities={openToJobOpportunities}
            onOpenToJobOpportunitiesToggle={setOpenToJobOpportunities}
            bizdevProcesses={bizdevProcesses}
            selectedBizdevProcesses={selectedBizdevProcesses}
            onBizdevProcessFilter={setSelectedBizdevProcesses}
          />
        ) : (
          <>
            {/* Overlay noir */}
            {showFilters && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={() => setShowFilters(false)}
              ></div>
            )}
            {/* Sidebar mobile/tablet */}
            <div
              className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto ${
                showFilters ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              {/* Header avec bouton fermer */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold text-gray-900 m-0 flex items-center gap-2">
                  <i className="bi bi-funnel"></i>
                  Filtres
                </h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="bi bi-x-lg text-xl"></i>
                </button>
              </div>

              {/* Contenu de FilterSidebar sans le wrapper */}
              <div className="py-4">
                <FilterSidebar
                  onTagFilter={setSelectedTags}
                  positions={positions}
                  onPositionFilter={setSelectedPositions}
                  showOnlyWithPhone={showOnlyWithPhone}
                  onPhoneFilter={setShowOnlyWithPhone}
                  showOnlyWithProfessionalEmail={showOnlyWithProfessionalEmail}
                  onProfessionalEmailToggle={setShowOnlyWithProfessionalEmail}
                  showOnlyWithPersonalEmail={showOnlyWithPersonalEmail}
                  onPersonalEmailToggle={setShowOnlyWithPersonalEmail}
                  currentTags={selectedTags}
                  selectedPositions={selectedPositions}
                  cityFilter={cityFilter}
                  onCityFilter={setCityFilter}
                  personalEmailFilter={personalEmailFilter}
                  onPersonalEmailFilter={setPersonalEmailFilter}
                  professionalEmailFilter={professionalEmailFilter}
                  onProfessionalEmailFilter={setProfessionalEmailFilter}
                  jobTitleFilter={jobTitleFilter}
                  onJobTitleFilter={setJobTitleFilter}
                  keywordFilter={keywordFilter}
                  onKeywordFilter={setKeywordFilter}
                  yearsOfExperienceFilter={yearsOfExperienceFilter}
                  onYearsOfExperienceFilter={setYearsOfExperienceFilter}
                  salaryFilters={salaryFilters}
                  onSalaryFiltersChange={setSalaryFilters}
                  openToJobOpportunities={openToJobOpportunities}
                  onOpenToJobOpportunitiesToggle={setOpenToJobOpportunities}
                  bizdevProcesses={bizdevProcesses}
                  selectedBizdevProcesses={selectedBizdevProcesses}
                  onBizdevProcessFilter={setSelectedBizdevProcesses}
                  isMobile={true}
                />
              </div>
            </div>
          </>
        )}

        {/* Colonne principale à droite avec recherche, actions et liste des individuals */}
        <div className="flex-1 relative">
          {/* Barre de recherche et actions */}

          <FilterBar
            selectedIndividuals={selectedIndividuals}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            onBulkActions={() => setShowBulkActions(true)}
          />
          {/* Informations de pagination */}
          <div className="flex justify-between items-center px-4 mb-2 text-sm text-gray-600 relative z-10">
            { viewType === 'grid' && (
              <div className=' flex items-center gap-2'>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectAll}
                  className='w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2'
                />
                <p>Tout sélectionner</p>
              </div>
            )}

            <div className="flex items-center gap-4 ">
              <div className="text-sm">
                <p>Affichage de {displayedIndividuals.length} sur {filteredIndividuals.length} contacts</p> 
              </div>

              {/* Sélecteur d'ordre de tri */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="updatedAt">Dernière modification</option>
                <option value="createdAt">Date d'ajout</option>
              </select>
            </div>

            <div>
              Page {currentPage} sur {totalPages || 1}
            </div>
          </div>
            
          {/* Vue conditionnelle : Tableau ou Grille */}
          <div className="relative isolate">
            {viewType === 'table' ? (
              <IndividualsTable
                individuals={displayedIndividuals}
                selectedIndividuals={selectedIndividuals}
                selectAll={selectAll}
                onSelectAll={handleSelectAll}
                onIndividualSelect={handleIndividualSelect}
                onIndividualClick={handleIndividualClick}
                onIndividualDelete={handleDelete}
                sortOrder={sortOrder}
                onSortByDate={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                getTagStyle={getTagStyle}
                screenSize={screenSize}
              />
            ) : (
              <IndividualsGrid
                individuals={displayedIndividuals}
                selectedIndividuals={selectedIndividuals}
                onIndividualSelect={handleIndividualSelect}
                onIndividualClick={handleIndividualClick}
                onIndividualDelete={handleDelete}
                getTagStyle={getTagStyle}
                selectAll={selectAll}
                onSelectAll={handleSelectAll}
                screenSize={screenSize}
              />
            )}
          </div>

          {/* Pagination */}
          {filteredIndividuals.length > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              individualsPerPage={itemsPerPage}
              totalIndividuals={filteredIndividuals.length}
              onPageChange={setCurrentPage}
              onLoadMore={() => setIndividualsPerPage(prev => prev + 20)}
            />
          )}
        </div>
      </div>

      {/* ✅ AMÉLIORATION: Modals complets */}
      {showAddForm && (
        <AddIndividual
          onClose={() => setShowAddForm(false)}
          refreshIndividuals={fetchIndividuals}
        />
      )}

      {showSendEmailModal && (
        <SendEmailModal
          recipients={filteredRecipientsForEmail}
          defaultEmailType={emailTypeForSending}
          onClose={() => setShowSendEmailModal(false)}
          onSend={() => setShowSendEmailModal(false)}
        />
      )}

      {/* Panel d'actions en lot */}
      {showBulkActions && (
        <BulkActionPanel
          selectedIndividuals={selectedIndividuals}
          onClose={() => setShowBulkActions(false)}
          onDelete={handleBulkDeleteRequest}
          onAddToProcess={handleBulkAddToProcess}
          onAddToBizDevProcess={handleBulkAddToBizDevProcess}
          onSendEmail={handleBulkSendEmail}
          onAddTags={handleBulkAddTags}
          onAddReminder={handleBulkAddReminder}
        />
      )}

      {/* Modale de confirmation de suppression en masse */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirmed}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer ${pendingDeleteIds.length} profil(s) ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Modale de confirmation de suppression individuelle */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setIndividualToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Supprimer le contact"
        message="Êtes-vous sûr de vouloir supprimer ce contact ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default AllIndividuals;